const IDLE_MS = 60 * 60 * 1000;
const ABSOLUTE_MS = 23 * 60 * 60 * 1000;
const READY_TIMEOUT_MS = 10000;
const UUID_V4 =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PRODUCTION_CLIENT_ID = "7e18fe8f-7889-4f00-932a-f7e411d547bf";
const SANDBOX_CLIENT_ID = "355d766e-f66d-4073-a442-4f0ea57366bc";

export function sessionIsFresh(session, now) {
  if (!session?.sessionId || !UUID_V4.test(session.sessionId)) return false;
  const issuedAt = Number(session.issuedAt);
  const activityAt = Number(session.activityAt);
  if (!issuedAt || !activityAt) return false;
  if (now - issuedAt >= ABSOLUTE_MS || now - activityAt >= IDLE_MS)
    return false;
  return true;
}

export function createSardineController({
  readSession,
  fetchSession,
  loadScript,
  getWindow,
  clientId,
  environment,
  apiSubdomain,
  pixelSubdomain,
  report = () => {},
  readyTimeoutMs = READY_TIMEOUT_MS
}) {
  let context = null;
  let initPromise = null;
  let queue = Promise.resolve();
  let collection = "degraded";
  let userIdHash;

  const environmentMatches =
    !(clientId === PRODUCTION_CLIENT_ID && environment === "sandbox") &&
    !(clientId === SANDBOX_CLIENT_ID && environment === "production");

  function enqueue(task) {
    const run = queue.then(task, task);
    queue = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }

  async function ensureSession(rotate = false) {
    const current = readSession();
    if (!rotate && sessionIsFresh(current, Date.now()))
      return current.sessionId;
    const issued = await fetchSession({
      rotate: rotate || !sessionIsFresh(current, Date.now())
    });
    return issued.sessionId;
  }

  async function ensureContext(sessionId) {
    if (context) return context;
    if (!environmentMatches) {
      report("environment_mismatch");
      throw new Error("environment_mismatch");
    }
    if (!initPromise) {
      initPromise = (async () => {
        await loadScript();
        const win = getWindow();
        if (typeof win._Sardine?.createContext !== "function") {
          report("init_failed");
          throw new Error("init_failed");
        }
        const options = {
          clientId,
          sessionKey: sessionId,
          flow: "startup",
          environment: environment === "sandbox" ? "sandbox" : "production",
          parentElement: win.document?.body,
          enablePortScanning: false
        };
        if (userIdHash) options.userIdHash = userIdHash;
        if (apiSubdomain) options.apiSubdomain = apiSubdomain;
        if (pixelSubdomain) options.pixelSubdomain = pixelSubdomain;
        const initialUpdate = trackConfigUpdate();
        options.onConfigUpdate = initialUpdate.onConfigUpdate;
        context = win._Sardine.createContext(options);
        win.sardineContext = context;
        await initialUpdate.done;
        return context;
      })().catch((error) => {
        initPromise = null;
        throw error;
      });
    }
    return initPromise;
  }

  function trackConfigUpdate() {
    let settled = false;
    let notify = () => {};
    const done = new Promise((resolve) => {
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        report("callback_timeout");
        resolve(false);
      }, readyTimeoutMs);
      notify = (event) => {
        if (settled) return;
        if (
          event !== "SESSION_UPDATED" &&
          event !== "SESSION_UPDATE_FAILED" &&
          event !== "SESSION_NO_UPDATE"
        ) {
          return;
        }
        settled = true;
        clearTimeout(timer);
        const ready =
          event === "SESSION_UPDATED" ||
          (event === "SESSION_NO_UPDATE" && collection === "ready");
        report(ready ? "session_updated" : "session_update_failed");
        resolve(ready);
      };
    });
    return {
      done,
      onConfigUpdate: (event) => notify(event)
    };
  }

  function waitForUpdate(sdk, fields) {
    if (typeof sdk?.updateConfig !== "function") return Promise.resolve(false);
    const tracked = trackConfigUpdate();
    sdk.updateConfig({
      ...fields,
      onConfigUpdate: tracked.onConfigUpdate
    });
    return tracked.done;
  }

  return {
    start() {
      return enqueue(async () => {
        try {
          const sessionId = await ensureSession(false);
          await ensureContext(sessionId);
        } catch (error) {
          collection = "degraded";
          report(error?.message || "init_failed");
        }
      });
    },
    prepare(flow, extra = {}) {
      return enqueue(async () => {
        try {
          const sessionId = await ensureSession(false);
          if (extra.userIdHash) userIdHash = extra.userIdHash;
          const sdk = await ensureContext(sessionId);
          const fields = { flow, sessionKey: sessionId };
          if (userIdHash) fields.userIdHash = userIdHash;
          const ready = await waitForUpdate(sdk, fields);
          collection = ready ? "ready" : "degraded";
          return { sessionId, ready, collection };
        } catch (error) {
          collection = "degraded";
          report(error?.message || "init_failed");
          return {
            sessionId: readSession()?.sessionId || "",
            ready: false,
            collection: "degraded"
          };
        }
      });
    },
    rotate() {
      return enqueue(async () => {
        userIdHash = undefined;
        const sessionId = await ensureSession(true);
        if (context) {
          await waitForUpdate(context, {
            sessionKey: sessionId,
            userIdHash: undefined
          });
        }
        collection = "degraded";
        return sessionId;
      });
    },
    setUser(id) {
      userIdHash = id || undefined;
      return enqueue(async () => {
        if (!context) return;
        try {
          await waitForUpdate(context, { userIdHash });
        } catch (error) {
          report(error?.message || "init_failed");
        }
      });
    },
    collectionHeader() {
      return collection;
    }
  };
}

function browserCookie(name) {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(
    new RegExp(
      "(^|;)\\s*" +
        name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") +
        "\\s*=\\s*([^;]+)"
    )
  );
  return match ? decodeURIComponent(match.pop()) : "";
}

function readBrowserSession() {
  return {
    sessionId: browserCookie("X-Session"),
    issuedAt: browserCookie("X-Session-Issued-At"),
    activityAt: browserCookie("X-Session-Timestamp")
  };
}

let browserController;

function browserSardine() {
  if (browserController) return browserController;
  const clientId = browserCookie("sardine_client_ID") || SANDBOX_CLIENT_ID;
  const environment = browserCookie("sardine_environment") || "sandbox";
  const apiSubdomain = browserCookie("sardine_api_subdomain");
  const pixelSubdomain = browserCookie("sardine_pixel_subdomain");
  const host =
    apiSubdomain ||
    (environment === "production"
      ? "api.sardine.ai"
      : "api.sandbox.sardine.ai");
  browserController = createSardineController({
    readSession: readBrowserSession,
    fetchSession: async ({ rotate }) => {
      const response = await fetch(
        "/risk/browser-session" + (rotate ? "?rotate=1" : ""),
        {
          credentials: "same-origin"
        }
      );
      if (!response.ok) throw new Error("cookie_missing");
      const body = await response.json();
      return { sessionId: body.session_id };
    },
    loadScript() {
      const src = "https://" + host + "/assets/loader.min.js";
      return new Promise((resolve, reject) => {
        const existing = document.querySelector('script[src="' + src + '"]');
        if (existing && window._Sardine) {
          resolve();
          return;
        }
        const loader = existing || document.createElement("script");
        const timer = setTimeout(
          () => reject(new Error("script_timeout")),
          READY_TIMEOUT_MS
        );
        const finish = (error) => {
          clearTimeout(timer);
          if (error) reject(error);
          else resolve();
        };
        loader.addEventListener("load", () => finish());
        loader.addEventListener("error", () =>
          finish(new Error("script_error"))
        );
        if (!existing) {
          loader.type = "text/javascript";
          loader.async = true;
          loader.src = src;
          const firstScript = document.getElementsByTagName("script")[0];
          if (firstScript?.parentNode) {
            firstScript.parentNode.insertBefore(loader, firstScript);
          } else {
            document.head.appendChild(loader);
          }
        }
      });
    },
    getWindow: () => window,
    clientId,
    environment,
    apiSubdomain,
    pixelSubdomain,
    report(outcome) {
      const body = JSON.stringify({ outcome });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          "/risk/browser-session/events",
          new Blob([body], { type: "application/json" })
        );
      }
    }
  });
  return browserController;
}

export function startSardine() {
  return browserSardine().start();
}

export function prepareRiskRequest(flow, extra) {
  return browserSardine().prepare(flow, extra);
}

export function rotateRiskSession() {
  return browserSardine().rotate();
}

export function setRiskUser(id) {
  browserSardine().setUser(id);
}

export function collectionHeader() {
  return browserController ? browserController.collectionHeader() : "degraded";
}

export function freshDevice(device = {}) {
  const sessionId = browserCookie("X-Session");
  const next = {
    ...device,
    user_agent:
      typeof navigator === "undefined"
        ? device.user_agent
        : navigator.userAgent
  };
  if (sessionId) next.session_id = sessionId;
  else delete next.session_id;
  return next;
}
